import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { FiPlus, FiX, FiHeart, FiShoppingBag, FiMapPin, FiZap, FiLink, FiChevronDown, FiLock } from 'react-icons/fi';
import { useSubscription } from '../../context/SubscriptionContext';

const CATEGORIES = ['Product', 'Treatment', 'Location'];
const categoryIcons = { Product: FiShoppingBag, Treatment: FiZap, Location: FiMapPin };
const categoryColors = {
  Product: 'bg-rose-50 text-rose-500 border-rose-200',
  Treatment: 'bg-amber-50 text-amber-600 border-amber-200',
  Location: 'bg-emerald-50 text-emerald-600 border-emerald-200',
};

const initialWishlist = [
  { id: 1, name: 'Skinceuticals CE Ferulic', category: 'Product', notes: 'Highly recommended for vitamin C', link: '' },
  { id: 2, name: 'Morpheus8', category: 'Treatment', notes: 'RF microneedling – want to try', link: '' },
  { id: 3, name: 'The Skin Lab Miami', category: 'Location', notes: 'Amazing reviews for HydraFacial', link: '' },
];

function AddItemForm({ onAdd, onCancel }) {
  const [form, setForm] = useState({ name: '', category: 'Product', notes: '', link: '' });
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="bg-stone-50 rounded-3xl p-4 space-y-3 border border-stone-200 mb-4">
      <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Name…"
        className="w-full font-sans text-sm bg-white border border-stone-200 rounded-2xl px-4 py-3 text-stone-700 focus:outline-none" />
      <div className="relative">
        <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
          className="w-full font-sans text-sm bg-white border border-stone-200 rounded-2xl px-4 py-3 text-stone-700 focus:outline-none appearance-none">
          {CATEGORIES.map(c => <option key={c}>{c}</option>)}
        </select>
        <SafeIcon icon={FiChevronDown} className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" />
      </div>
      <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Notes (optional)…"
        className="w-full font-sans text-sm bg-white border border-stone-200 rounded-2xl px-4 py-3 text-stone-700 focus:outline-none" />
      <input value={form.link} onChange={e => setForm({ ...form, link: e.target.value })} placeholder="Link (optional)…"
        className="w-full font-sans text-sm bg-white border border-stone-200 rounded-2xl px-4 py-3 text-stone-700 focus:outline-none" />
      <div className="flex gap-2">
        <button onClick={onCancel} className="flex-1 py-3 rounded-2xl border border-stone-200 text-stone-500 font-sans text-sm active:bg-stone-50">Cancel</button>
        <button onClick={() => form.name.trim() && onAdd({ ...form, id: Date.now() })}
          className="flex-1 py-3 rounded-2xl bg-stone-900 text-white font-sans text-sm font-medium active:bg-stone-700">Add to Wishlist</button>
      </div>
    </motion.div>
  );
}

function WishlistLockScreen({ onOpenPricing }) {
  return (
    <div className="h-full flex flex-col items-center justify-center px-8 text-center relative overflow-hidden bg-stone-50">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-20%] w-[70%] h-[50%] rounded-full bg-[#ffe0d2] blur-3xl opacity-40" />
        <div className="absolute bottom-[-10%] left-[-20%] w-[70%] h-[60%] rounded-full bg-[#d1ece5] blur-3xl opacity-40" />
      </div>

      {/* Blurred wishlist preview */}
      <div className="relative w-full max-w-xs mb-8">
        <div className="w-full rounded-3xl bg-white border border-stone-100 shadow-sm overflow-hidden">
          {['Skinceuticals CE Ferulic', 'Morpheus8', 'The Skin Lab Miami'].map((name) => (
            <div key={name} className="flex items-center gap-3 px-4 py-3.5 border-b border-stone-50 last:border-0">
              <div className="w-8 h-8 rounded-full bg-rose-50" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3 bg-stone-100 rounded-full w-3/4" />
                <div className="h-2 bg-stone-50 rounded-full w-1/2" />
              </div>
            </div>
          ))}
          <div className="absolute inset-0 backdrop-blur-sm bg-white/40 rounded-3xl" />
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-14 h-14 rounded-full bg-white/90 shadow-lg flex items-center justify-center">
            <SafeIcon icon={FiLock} className="text-2xl text-stone-600" />
          </div>
        </div>
      </div>

      <span className="text-4xl mb-4">🌟</span>
      <h2 className="font-serif text-3xl text-stone-900 mb-3">Your Glow Wishlist</h2>
      <p className="font-sans text-sm text-stone-500 leading-relaxed mb-8 max-w-[280px]">
        Save products, treatments, and spa locations you want to try on your skin journey.
      </p>

      <motion.button
        whileTap={{ scale: 0.97 }}
        onClick={onOpenPricing}
        className="px-8 py-4 rounded-full font-sans font-semibold text-white text-base shadow-lg"
        style={{ background: 'linear-gradient(135deg, #5C8A78, #7ecdc4)' }}
      >
        Unlock with Glow — $14.99/yr
      </motion.button>
      <p className="font-sans text-xs text-stone-400 mt-3">~$1.25/month · Cancel anytime</p>
    </div>
  );
}

export default function WishlistView({ onOpenPricing }) {
  const { can } = useSubscription();
  const [items, setItems] = useState(initialWishlist);
  const [filter, setFilter] = useState('All');
  const [showForm, setShowForm] = useState(false);

  if (!can('wishlist')) {
    return <WishlistLockScreen onOpenPricing={onOpenPricing} />;
  }

  const filtered = filter === 'All' ? items : items.filter(i => i.category === filter);
  const addItem = (item) => { setItems(prev => [item, ...prev]); setShowForm(false); };
  const removeItem = (id) => setItems(prev => prev.filter(i => i.id !== id));

  return (
    <div className="h-full overflow-y-auto pb-4 px-5 bg-stone-50">
      <div className="pt-6 pb-5 flex items-center gap-3">
        <img
          src="https://media-manager-c.questera.ai/greta-media/1593c15ad88356866a2eabcebc8aef2c67d9fc5afee8d56439ced245a0c6c3417efffbd95aedd1b6f68107af9f17a1fd/images/aW1hZ2UvcG5n/44d2d46ca6ce6a8943aba646038b0408.png"
          alt="Glowlytix Logo"
          className="w-10 h-10 object-contain drop-shadow-sm flex-shrink-0"
        />
        <div>
          <p className="font-sans text-xs text-stone-400 uppercase tracking-widest font-medium mb-0.5">Save for Later</p>
          <h1 className="font-serif text-3xl text-stone-900 leading-none">Wishlist</h1>
        </div>
      </div>

      <div className="flex gap-2 mb-5 overflow-x-auto pb-1">
        {['All', ...CATEGORIES].map(cat => (
          <button key={cat} onClick={() => setFilter(cat)}
            className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-sans font-medium border transition-all active:scale-95 ${filter === cat ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-500 border-stone-200'}`}>
            {cat}
          </button>
        ))}
      </div>

      <AnimatePresence>
        {showForm && <AddItemForm onAdd={addItem} onCancel={() => setShowForm(false)} />}
      </AnimatePresence>

      {!showForm && (
        <button onClick={() => setShowForm(true)}
          className="w-full py-4 rounded-3xl border-2 border-dashed border-stone-200 text-stone-400 font-sans text-sm flex items-center justify-center gap-2 mb-4 active:bg-stone-100 transition-colors">
          <SafeIcon icon={FiPlus} /> Add to Wishlist
        </button>
      )}

      <div className="space-y-3">
        {filtered.map(item => {
          const Icon = categoryIcons[item.category] || FiHeart;
          return (
            <motion.div key={item.id} layout initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
              className="bg-white rounded-3xl p-4 border border-stone-100 shadow-sm flex items-start gap-3">
              <div className={`w-10 h-10 rounded-2xl border flex items-center justify-center flex-shrink-0 ${categoryColors[item.category]}`}>
                <SafeIcon icon={Icon} className="text-base" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-sans font-semibold text-stone-800 text-sm">{item.name}</p>
                <span className={`inline-block text-xs px-2 py-0.5 rounded-full border font-sans mt-0.5 ${categoryColors[item.category]}`}>{item.category}</span>
                {item.notes && <p className="text-xs text-stone-400 font-sans mt-1">{item.notes}</p>}
                {item.link && (
                  <a href={item.link} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-rose-400 font-sans mt-1">
                    <SafeIcon icon={FiLink} className="text-xs" /> Visit link
                  </a>
                )}
              </div>
              <button onClick={() => removeItem(item.id)} className="text-stone-300 p-1 flex-shrink-0 active:text-stone-500">
                <SafeIcon icon={FiX} className="text-lg" />
              </button>
            </motion.div>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-center py-16 text-stone-300">
            <SafeIcon icon={FiHeart} className="text-4xl mx-auto mb-3" />
            <p className="font-sans text-sm">Nothing saved yet</p>
          </div>
        )}
      </div>
    </div>
  );
}